<script setup>
import { nextTick, ref } from 'vue';
import CatalogFilter from '@/components/catalog/CatalogFilter.vue';
import CatalogFilterPrice from '@/components/catalog/CatalogFilterPrice.vue';

const props = defineProps({
  type: { type: String, required: true },
  draftFilters: { type: Object, required: true },
  forFilters: { type: Object, default: null },
  allFilterValues: { type: Object, default: null },
  activeFilterCount: { type: Number, default: 0 },
  hasTransport: { type: Boolean, default: true },
  filterShowUrl: { type: Boolean, default: false },
  getLinkFn: { type: Function, default: () => '#' },
  draftApiUrl: { type: String, default: null },
  fetchPreviewFn: { type: Function, default: null },
  resetPreviewFn: { type: Function, default: null },
});

const emit = defineEmits(['set-draft-filter', 'cancel', 'submit', 'clear']);

const isOpen = ref(false);

function openPanel() {
  emit('cancel');
  isOpen.value = true;
  document.body.style.overflow = 'hidden';
}

function cancelAndClose() {
  emit('cancel');
  if (props.resetPreviewFn) props.resetPreviewFn();
  isOpen.value = false;
  document.body.style.overflow = '';
}

function submitAndClose() {
  emit('submit');
  isOpen.value = false;
  document.body.style.overflow = '';
}

function setDraftFilter(field, value) {
  emit('set-draft-filter', field, value);
  if (props.fetchPreviewFn && props.draftApiUrl){
    nextTick(() => {
      props.fetchPreviewFn(props.draftApiUrl)
    })
  }
}
</script>

<template>
  <div class="catalog__panel-all">
    <Button
      class="button-transparent catalog__panel-all-button js-button-filters"
      @click="openPanel"
    >
      <span class="catalog__panel-all-text link-normal">{{
        $t('filters.all_filters')
      }}</span>
      <CIcon
        name="filter"
        width="16"
        height="16"
        class="catalog__panel-all-icon"
      />
    </Button>

    <Teleport to="body">
      <Transition name="overlay-fade">
        <div
          v-if="isOpen"
          class="catalog__filters-overlay"
          @click="cancelAndClose"
        />
      </Transition>

      <Transition name="panel-slide">
        <div v-if="isOpen" class="catalog__filters">
          <ScrollPanel style="width: 100%; max-height: 100vh">
            <div class="catalog__filters-header">
              <span class="catalog__filters-header-title h6-mobile">{{
                $t('filters.all_filters')
              }}</span>
              <button
                type="button"
                class="catalog__filters-header-button js-button-filters-close"
                @click="cancelAndClose"
              >
                <CIcon
                  class="catalog__filters-header-button-icon"
                  name="close-black"
                  width="16"
                  height="16"
                />
              </button>
            </div>
            <CatalogFilter
              v-if="forFilters?.brand"
              :label="$t('filters.brand')"
              :options="forFilters?.brand || []"
              :active-options="forFilters?.brand || []"
              :selected="draftFilters.brand"
              :filter-key="'brand'"
              :filter-show-url="filterShowUrl.brand"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('brand', val)"
            />
            <CatalogFilter
              v-if="forFilters?.model"
              :label="$t('filters.model')"
              :options="forFilters?.model || []"
              :active-options="forFilters?.model || []"
              :selected="draftFilters.model"
              :filter-key="'model'"
              :filter-show-url="filterShowUrl.model"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('model', val)"
            />
            <CatalogFilterPrice
              v-if="type === 'stock'"
              :min="forFilters?.price?.min ?? allFilterValues?.price?.min"
              :max="forFilters?.price?.max ?? allFilterValues?.price?.max"
              :value-from="draftFilters.price_from"
              :value-to="draftFilters.price_to"
              @change="({ from, to }) => {
                setDraftFilter('price_from', from)
                setDraftFilter('price_to', to)
              }"
            />
            <CatalogFilter
              v-if="forFilters?.color"
              :label="$t('filters.color')"
              :options="forFilters?.color || []"
              :active-options="forFilters?.color || []"
              :selected="draftFilters.color"
              :filter-key="'color'"
              :filter-show-url="filterShowUrl.color"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('color', val)"
            />
            <CatalogFilter
              v-if="forFilters?.bodytype"
              :label="$t('filters.bodytype')"
              :options="forFilters?.bodytype || []"
              :active-options="forFilters?.bodytype || []"
              :selected="draftFilters.bodytype"
              :filter-key="'bodytype'"
              :filter-show-url="filterShowUrl.bodytype"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('bodytype', val)"
            />
            <CatalogFilter
              v-if="forFilters?.transmission"
              :label="$t('filters.transmission')"
              :options="forFilters?.transmission || []"
              :active-options="forFilters?.transmission || []"
              :selected="draftFilters.transmission"
              :filter-key="'transmission'"
              :filter-show-url="filterShowUrl.transmission"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('transmission', val)"
            />
            <CatalogFilter
              v-if="forFilters?.wheel_drive"
              :label="$t('filters.wheel_drive')"
              :options="forFilters?.wheel_drive || []"
              :active-options="forFilters?.wheel_drive || []"
              :selected="draftFilters.wheel_drive"
              :filter-key="'wheel_drive'"
              :filter-show-url="filterShowUrl.wheel_drive"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('wheel_drive', val)"
            />
            <CatalogFilter
              v-if="forFilters?.fuel"
              :label="$t('filters.fuel')"
              :options="forFilters?.fuel || []"
              :active-options="forFilters?.fuel || []"
              :selected="draftFilters.fuel"
              :filter-key="'fuel'"
              :filter-show-url="filterShowUrl.fuel"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('fuel', val)"
            />
            <CatalogFilter
              v-if="forFilters?.region"
              :label="$t('filters.region')"
              :options="forFilters?.region || []"
              :active-options="forFilters?.region || []"
              :selected="draftFilters.region"
              :filter-key="'region'"
              :filter-show-url="filterShowUrl.region"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('region', val)"
            />
            <CatalogFilter
              v-if="forFilters?.dealer"
              :label="$t('filters.dealer')"
              :options="forFilters?.dealer || []"
              :active-options="forFilters?.dealer || []"
              :selected="draftFilters.dealer"
              :filter-key="'dealer'"
              :filter-show-url="filterShowUrl.dealer"
              :get-link-fn="getLinkFn"
              multi
              @change="(val) => setDraftFilter('dealer', val)"
            />
            <CatalogFilterPrice
              v-if="type !== 'stock'"
              :min="forFilters?.price?.min ?? allFilterValues?.price?.min"
              :max="forFilters?.price?.max ?? allFilterValues?.price?.max"
              :value-from="draftFilters.price_from"
              :value-to="draftFilters.price_to"
              @change="({ from, to }) => {
                setDraftFilter('price_from', from)
                setDraftFilter('price_to', to)
              }"
            />
            <div class="catalog__filters-actions">
              <Button
                rounded
                class="catalog__filters-button button-black d-block"
                @click="submitAndClose"
                >{{ $t('filters.apply') }}</Button
              >
            </div>
          </ScrollPanel>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.catalog {
  &__panel {
    &-all-button {
      display: flex;
      justify-content: space-between;
      width: 100%;
    }
  }
  &__filters {
    background-color: $white;
    display: block;
    grid-area: filters;
    inset: 0;
    left: 0;
    max-width: 384px;
    position: fixed;
    top: 0;
    z-index: 1001;
    &-header {
      align-items: center;
      background-color: $white;
      border-bottom: 1px solid $divider;
      border-radius: 4px 4px 0 0;
      display: grid;
      grid-template-columns: auto 32px;
      justify-content: space-between;
      padding: 18px 16px 15px 24px;
      position: sticky;
      top: 0;
      z-index: 2;
      &-button {
        align-items: center;
        display: flex;
        cursor: pointer;
        justify-content: center;
        height: 32px;
        width: 32px;
      }
    }
    &-actions {
      background-color: $white;
      bottom: 0;
      padding: 32px 16px 96px;
      position: sticky;
      z-index: 2;
      &:before {
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          #ffffff 25%
        );
        bottom: 144px;
        content: '';
        display: block;
        height: 50px;
        left: 0;
        position: absolute;
        width: 100%;
        z-index: 1;
      }
      &:after {
        background-color: $white;
        bottom: 0;
        content: '';
        display: block;
        height: 64px;
        left: 0;
        position: absolute;
        width: 100%;
        z-index: -1;
      }
    }
    &-button {
      width: 100%;
    }
    &:deep(
        .catalog__filters-header
          + .catalog__filter-container
          .catalog__filter-top
      ) {
      top: 65px;
    }
    &-overlay {
      background-color: rgba(0, 0, 0, 0.7);
      position: fixed;
      inset: 0;
      z-index: 1000;
    }
  }
}

/* Transitions */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.panel-slide-enter-from,
.panel-slide-leave-to {
  /* -100% ховає панель повністю вліво за межі екрана */
  transform: translateX(-100%);
}

/* Стан після завершення анімації (необов'язково, але корисно для розуміння) */
.panel-slide-enter-to,
.panel-slide-leave-from {
  transform: translateX(0);
}
</style>
